import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Badge de estado del Design System.
 *
 * Alto 21px, radio 6, 11.5px/500. Fondo suave + texto saturado del mismo tono:
 * NUNCA texto blanco sobre color saturado. El punto de 5px va a la izquierda y
 * se activa con `dot`.
 *
 * El color acompaña al dato, no lo reemplaza: el badge siempre lleva su texto.
 */
const badgeVariants = cva(
  "inline-flex h-[21px] w-fit shrink-0 items-center justify-center gap-[5px] whitespace-nowrap rounded-sm px-2 text-[11.5px] font-medium [&>svg]:size-3 [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        /** Subido · Activo · Aprobado */
        success: "bg-success-bg text-success",
        /** Por vencer · Iniciado */
        warning: "bg-warning-bg text-warning",
        /** Vencido · Reprobado · Atrasado */
        destructive: "bg-destructive-bg text-destructive",
        /** Pendiente · Cerrada */
        neutral: "bg-muted text-muted-foreground",
        /** No aplica — sale del denominador, no genera alerta. */
        muted: "bg-neutral-bg text-soft",
        /** Listo */
        info: "bg-info-bg text-info",
        /** Generando · En curso */
        brand: "bg-primary-100 text-primary-hover",
        /** Finalizado */
        navy: "bg-navy-bg text-foreground",
        outline: "border border-input bg-background text-foreground",

        // Alias heredados de shadcn. El DS no tiene badges de fondo saturado:
        // `default` es el tono de marca suave y `secondary` el neutro.
        default: "bg-primary-100 text-primary-hover",
        secondary: "bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  }
)

type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["variant"]>

function Badge({
  className,
  variant,
  dot = false,
  pulse = false,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** Punto de 5px a la izquierda — el patrón de badge de estado. */
    dot?: boolean
    /** Latido del punto mientras algo se está generando. */
    pulse?: boolean
  }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {dot && (
        <span
          aria-hidden
          className={cn(
            "size-[5px] shrink-0 rounded-full bg-current",
            pulse && "animate-fc-pulse"
          )}
        />
      )}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants, type BadgeTone }
